import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ColDef, GridApi, GridReadyEvent, ICellRendererParams, IDatasource, IGetRowsParams } from 'ag-grid-community';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AgGridModule } from 'ag-grid-angular';

import { ApiUrlService } from 'src/app/services/api-url.service';
import { ApiService } from 'src/app/services/api.service';
import { DataService } from '../../services/data.service';
import { LoadingService } from '../../services/loading.service';

import { PartnerLogColDef } from 'src/app/models/partner-log-col-defs.model';
import { AgPaginationToolbarComponent } from 'src/app/shared/ui/ag-pagination-toolbar/ag-pagination-toolbar.component';
import { buildQueryParamsFromFilterModel, formatGridValueOrNA, getAgGridFilterType } from 'src/app/shared/ui/grid-format.util';
import { ResponseMessageCellRendererComponent } from 'src/app/shared/ui/response-message-cell-renderer.component';
import { UiModule } from 'src/app/shared/ui/ui.module';

@Component({
  standalone: true,
    selector: 'app-ema-batch-view-errors',
    imports: [
        CommonModule,
        AgGridModule,
        AgPaginationToolbarComponent,
        UiModule,
        FormsModule,
    ],
    templateUrl: './ema-batch-view-errors.component.html',
    styleUrl: './ema-batch-view-errors.component.scss'
})
export class EmaBatchViewErrorsComponent implements OnInit, OnDestroy, AfterViewInit {
  batchId: string | null = null;
  rowData: any[] = [];
  totalRowCount = 0;
  currentPage = 1;
  startDate?: Date;
  endDate?: Date;

  gridApi!: GridApi;
  gridOptions: any;
  columnDefs: ColDef[] = [];
  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: false
  };
  materialCrumbs = [
    { label: 'Home', link: '/' },
    { label: 'Batch Headers', link: null } // Current page, no link
  ];
  gridContext = { componentParent: this };
  private isTabActivated = false;
  private isNavigatingToPage = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public loadingService: LoadingService,
    private dataService: DataService,
    private apiService: ApiService,
    private dialog: MatDialog,
    private http: HttpClient,
    private apiUrlService: ApiUrlService
  ) { }

  ngOnInit() {
    this.batchId = this.route.snapshot.paramMap.get('batchId');
    this.setupGrid();
    window.addEventListener('resize', this.onWindowResize);
  }

  ngAfterViewInit(): void {
    console.log('AfterViewInit called for errors component');
    // Don't initialize grid or size columns until tab is activated
    // The grid will be initialized when onTabActivated is called
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.onWindowResize);
  }

  private safeGridApiCall(callback: (api: GridApi) => void): void {
    if (this.gridApi && !this.gridApi.isDestroyed()) {
      callback(this.gridApi);
    }
  }

  // Safely resize columns to fit content width
  private safelySizeColumnsToFit(): void {
    const checkAndSize = (attempts = 0) => {
      // Check if the errors tab is actually visible before proceeding
      const errorsTabContent = document.querySelector('mat-tab-body.mat-tab-body-active app-ema-batch-view-errors');
      if (!errorsTabContent) {
        console.log('Errors tab not visible, skipping column sizing');
        return;
      }

      const gridElement = document.querySelector('app-ema-batch-view-errors .ag-root-wrapper') as HTMLElement;

      if (gridElement && gridElement.offsetWidth > 0) {
        // Wrap the API call in a timeout to avoid render conflicts
        setTimeout(() => {
          try {
            this.safeGridApiCall(api => api.autoSizeAllColumns());
          } catch (error) {
            console.warn('Failed to auto-size columns:', error);
          }
        }, 0);
      } else if (attempts < 10) { // Limit retry attempts
        // Retry after a short delay
        setTimeout(() => checkAndSize(attempts + 1), 200); // Increased delay
      } else {
        console.warn('Grid width still zero after multiple attempts');
      }
    };

    checkAndSize();
  }

  public onTabActivated(): void {
    const wasFirstActivation = !this.isTabActivated;
    this.isTabActivated = true;

    // Initialize grid datasource on first activation
    if (wasFirstActivation && this.gridApi) {
      this.initializeGridDatasource();
      return;
    }

    // Only proceed if the grid is ready and this tab is visible
    if (!this.gridApi) {
      console.warn('Grid API not ready during tab activation');
      return;
    }

    // Use longer timeout to ensure tab transition is complete and tab is visible
    setTimeout(() => {
      // Check if the errors tab is actually visible before sizing columns
      const errorsTabContent = document.querySelector('mat-tab-body.mat-tab-body-active app-ema-batch-view-errors');
      if (errorsTabContent) {
        this.safelySizeColumnsToFit();

        // Then refresh cache in a separate timeout to avoid conflicts
        setTimeout(() => {
          this.safeGridApiCall(api => api.refreshInfiniteCache());
        }, 300);
      } else {
        console.log('Errors tab not visible, skipping column sizing');
      }
    }, 300); // Increased timeout to ensure tab is fully visible
  }

  setupGrid(): void {

    // Remove custom filterpath from PartnerLogColDef before assigning to grid
    const strippedColDefs = PartnerLogColDef.map(({ filterPath, ...colDef }) => colDef);
    this.columnDefs = [
      {
        headerName: "",
        maxWidth: 10,
        // it is important to have node.id here, so that when the id changes (which happens
        // when the row is loaded) then the cell is refreshed.
        valueGetter: "node.id",
        cellRenderer: (params: ICellRendererParams) => {
          if (params.value !== undefined) {
            return "";
          } else {
            return '<img src="https://www.ag-grid.com/example-assets/loading.gif">';
          }
        },
      },
      ...strippedColDefs.map(col => ({
        ...col,
        lockPosition: ['left', 'right'].includes(col.lockPosition ?? '')
          ? col.lockPosition as 'left' | 'right'
          : undefined,
        tooltipValueGetter: formatGridValueOrNA,
        filter: getAgGridFilterType(col.cellDataType),
        floatingFilter: false,
        valueFormatter: col.cellRenderer ? undefined : formatGridValueOrNA, // Don't format if using custom renderer
        cellRenderer: col.cellRenderer === 'ResponseMessageCellRenderer'
          ? ResponseMessageCellRendererComponent
          : col.cellRenderer,
        filterParams: {
          maxNumConditions: 1,
          ...col.filterParams // Preserve any existing filterParams
        },
        sortable: col.sortable ?? true,
        resizable: true,
        flex: col.flex,
        minWidth: col.minWidth,
        // Preserve autoHeight and wrapText properties
        autoHeight: col.autoHeight,
        wrapText: col.wrapText,
      }))
    ];

    this.gridOptions = {
      rowModelType: 'infinite',
      pagination: true,
      paginationPageSize: 50,
      paginationPageSizeSelector: false, // Disable AG Grid's built-in pagination selector since we use custom toolbar
      cacheBlockSize: 50,
      context: this.gridContext,
      suppressColumnVirtualisation: true, // Disable column virtualization for autoSizeAllColumns to work on all columns
      onFirstDataRendered: (params: any) => {
        // Auto-size all columns to content width once data is rendered
        this.safeGridApiCall(api => {
          api.autoSizeAllColumns();
        });
      },
      dataTypeDefinitions: {
        string: {
          baseDataType: 'text',
          extendsDataType: 'text',
          valueParser: (params: any) => params.newValue,
          valueFormatter: (params: any) => params.value
        },
        integer: {
          baseDataType: 'number',
          extendsDataType: 'number',
          valueParser: (params: any) => parseInt(params.newValue),
          valueFormatter: (params: any) => params.value
        },
        date: {
          baseDataType: 'date',
          extendsDataType: 'date',
          valueParser: (params: any) => new Date(params.newValue),
          valueFormatter: (params: any) => params.value
        }
      },
      rowHeight: 150, // Default row height for all rows
      // Enable auto row height for cells with dynamic content
      getRowHeight: (params: any) => {
        // Check if this row has a response_message that might need more height
        const responseMessage = params.data?.response_message;

        if (responseMessage && typeof responseMessage === 'string' && responseMessage.includes('errors')) {
          try {
            // Try to parse the JSON to count errors
            let cleanedValue = responseMessage.trim();
            if (cleanedValue.startsWith('"') && cleanedValue.endsWith('"')) {
              cleanedValue = cleanedValue.slice(1, -1);
            }
            cleanedValue = cleanedValue.replace(/\\"/g, '"').replace(/\\n/g, '');

            const parsed = JSON.parse(cleanedValue);
            if (parsed.errors && Array.isArray(parsed.errors)) {
              // Give enough height for error count + first error
              return 70;
            }
          } catch (e) {
            // If parsing fails, give a reasonable height
            return 60;
          }
        }
        return 50; // Return the same default row height as specified in gridOptions
      },
      // Ensure the grid recalculates when data changes
      suppressRowHoverHighlight: false
    };

  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;

    // Only initialize datasource if tab is activated
    if (this.isTabActivated) {
      this.initializeGridDatasource();
    }
  }

  private initializeGridDatasource(): void {
    if (!this.gridApi) return;

    // Use longer timeout for grid ready to ensure everything is initialized
    setTimeout(() => {
      // Only try to size if grid is visible or if it's the active tab
      const isActiveTab = document.querySelector('mat-tab-body.mat-tab-body-active .ag-root-wrapper');

      if (isActiveTab) {
        this.safelySizeColumnsToFit();
      }

      // Set datasource in a separate timeout to avoid conflicts
      setTimeout(() => {
        if (this.gridApi) {
          const datasource: IDatasource = {
            getRows: (params: IGetRowsParams) => {
              const page = Math.floor(params.startRow / this.gridOptions.cacheBlockSize) + 1;
              this.fetchPartnerLogsData(page, params, params.sortModel, params.filterModel);
            }
          };
          this.gridApi.updateGridOptions({ datasource });
        }
      }, 50);
    }, 200);
  }

  fetchPartnerLogsData(page: number, params: IGetRowsParams, sortModel: any = [], filterModel: any = {}): void {

    // Create a lookup map for column definitions
    const columnDefMap = new Map();
    PartnerLogColDef.forEach(colDef => {
      columnDefMap.set(colDef.field, colDef);
    });

    // Build sort query using filterPath and proper format
    const sortQueries = sortModel.map((s: any) => {
      const colDef = columnDefMap.get(s.colId);
      const fieldPath = colDef?.filterPath || s.colId;
      const prefix = s.sort === 'desc' ? '-' : '';
      return `order_by=${prefix}${fieldPath}`;
    });

    const limit = this.gridOptions.cacheBlockSize;

    const queryParams = buildQueryParamsFromFilterModel(filterModel);
    queryParams.append('page', String(page));
    queryParams.append('limit', String(limit));
    // Add sort parameters
    sortQueries.forEach((sortQuery: { split: (arg0: string) => [any, any]; }) => {
      const [key, value] = sortQuery.split('=');
      queryParams.append(key, value);
    });

    const queryString = "logtype=errors&" + queryParams.toString();

    var endpoint = this.apiUrlService.getUrl(
      'gatewayBaseUrl',                        // serviceKey from environment.apiBaseUrls
      'partnerLogs',                   // endpointKey from environment.apiEndpoints
      { batchId: this.batchId! },     // pathParams (replace :batchId in the endpoint)
      queryString                    // query string
    );

    this.rowData = [];

    this.apiService.getRequest(endpoint).subscribe({
      next: (response: any) => {
        const items = response.logs || [];
        this.totalRowCount = response.count || 0;

        // Only update currentPage if we're not navigating to a specific page
        if (!this.isNavigatingToPage) {
          this.currentPage = response.page ?? 1;
        } else {
          // Reset the flag after navigation
          this.isNavigatingToPage = false;
        }

        if (items.length === 0) {
          setTimeout(() => {
            params.successCallback([], 0);
            this.safeGridApiCall(api => api.showNoRowsOverlay());
          }, 0);
          return;
        }
        setTimeout(() => {
          this.safeGridApiCall(api => api.hideOverlay());
        }, 0);
        params.successCallback(items, this.totalRowCount);
      },
      error: () => {
        params.failCallback();
        this.rowData = [];
      }
    });
  }

  applyFilter(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    setTimeout(() => {
      this.safeGridApiCall(api => api.setQuickFilter(value.trim().toLowerCase()));
    }, 0);
  }

  onPageSizeChanged(newSize: number): void {
    this.gridOptions.cacheBlockSize = newSize;
    this.gridOptions.paginationPageSize = newSize;
    this.currentPage = 1;

    // Update the grid options
    setTimeout(() => {
      this.safeGridApiCall(api => {
        api.updateGridOptions({
          cacheBlockSize: newSize,
          paginationPageSize: newSize
        });
        api.purgeInfiniteCache();
      });
    }, 0);
  }

  onGoToPage(pageNumber: number): void {
    if (!this.isTabActivated) {
      return;
    }

    if (!this.gridApi) {
      return;
    }

    // Set a flag to prevent the fetch method from overriding our currentPage
    this.isNavigatingToPage = true;
    this.currentPage = pageNumber;

    // Try ag-Grid's built-in pagination methods first
    try {
      if ((this.gridApi as any).paginationGoToPage) {
        const targetPage = pageNumber - 1; // ag-Grid uses 0-based indexing
        (this.gridApi as any).paginationGoToPage(targetPage);
        return;
      }
    } catch (e) {
      // Pagination method failed, fall back to infinite scroll approach
      console.error('paginationGoToPage failed:', e);
    }

    // Try ag-Grid's pagination API
    try {
      if ((this.gridApi as any).paginationSetPage) {
        const targetPage = pageNumber - 1; // ag-Grid uses 0-based indexing
        (this.gridApi as any).paginationSetPage(targetPage);
        return;
      }
    } catch (e) {
      // Pagination method failed, fall back to infinite scroll approach
      console.error('paginationSetPage failed:', e);
    }

    // Fallback to infinite scroll approach
    const startRow = (pageNumber - 1) * this.gridOptions.cacheBlockSize;

    // Clear the cache and try to navigate
    this.safeGridApiCall(api => api.purgeInfiniteCache());

    setTimeout(() => {
      this.safeGridApiCall(api => {
        // Reset the datasource
        const datasource: IDatasource = {
          getRows: (params: IGetRowsParams) => {
            const page = Math.floor(params.startRow / this.gridOptions.cacheBlockSize) + 1;
            this.fetchPartnerLogsData(page, params, params.sortModel, params.filterModel);
          }
        };

        api.updateGridOptions({ datasource });

        // Try to navigate after datasource is set
        setTimeout(() => {
          this.safeGridApiCall(innerApi => {
            if (startRow < this.totalRowCount) {
              innerApi.ensureIndexVisible(startRow, 'top');
            }
          });
        }, 300);
      });
    }, 100);
  }

  onCrumbClicked(event: any) {
    if (event.link) {
      this.router.navigate([event.link]);
    }
  }

  onWindowResize = () => {
    if (this.gridApi) {
      setTimeout(() => {
        this.safelySizeColumnsToFit();
      }, 100); // Delay to ensure grid is fully rendered
    }
  }

  onExport(): void {
    setTimeout(() => {
      this.safeGridApiCall(api => {
        const now = new Date();
        const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;

        api.exportDataAsCsv({
          fileName: `batch-${this.batchId}-errors-export-${timestamp}.csv`,
          allColumns: true,
          onlySelected: false,
          skipRowGroups: true,
          processCellCallback: (params) => {
            return params.value != null ? params.value : '';  // Avoid N/A
          },
        });
      });
    }, 0);
  }

}
